import { useState } from "react";
import {
  Box,
  Button,
  TextField,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";
import { Edit, Visibility, VisibilityOff, Close } from "@mui/icons-material";
import { generateRandomPassword } from "../../utils/password-generator";
import { makeSxStyles } from "ui";

interface PasswordEditProps {
  onPasswordChange: (password: string) => void;
  onCancel?: () => void;
  initialPassword?: string;
  error?: boolean;
  helperText?: string;
}

const PasswordEdit = ({
  onPasswordChange,
  onCancel,
  initialPassword = "",
  error = false,
  helperText,
}: PasswordEditProps) => {
  const styles = useStyles();
  const [isEditing, setIsEditing] = useState(false);
  const [password, setPassword] = useState(initialPassword);
  const [showPassword, setShowPassword] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = event.target.value;
    setPassword(newPassword);
    setShowWarning(newPassword.length > 0);
    onPasswordChange(newPassword);
  };

  const handleGeneratePassword = () => {
    const newPassword = generateRandomPassword();
    setPassword(newPassword);
    setShowWarning(true);
    onPasswordChange(newPassword);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setPassword(initialPassword);
    setShowWarning(false);
    onCancel?.();
  };

  const handleCloseWarning = () => {
    setShowWarning(false);
  };

  if (!isEditing) {
    return (
      <Button
        variant="outlined"
        startIcon={<Edit />}
        onClick={handleEditClick}
        sx={{ justifySelf: "flex-start" }}
      >
        Edit Password
      </Button>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          position: "relative",
        }}
      >
        <TextField
          label="Password"
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={handlePasswordChange}
          margin="dense"
          variant="outlined"
          fullWidth
          error={error}
          helperText={helperText}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {!showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button
          sx={{
            position: "absolute",
            left: "100%",
            top: "50%",
            transform: "translateY(-50%)",
            marginLeft: "16px",
          }}
          onClick={handleGeneratePassword}
        >
          Generate
        </Button>
      </Box>

      {showWarning && (
        <Box sx={styles.invalidSelection}>
          <Box>
            <Typography
              color="#690005"
              fontWeight={600}
              data-testid="invalid-selection-body"
            >
              Driver access will be interrupted
            </Typography>
            <Typography
              color="#690005"
              fontWeight={400}
              data-testid="invalid-selection-body"
            >
              Once changes are saved, the driver will be logged out and required
              to sign in again.
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            size="small"
            onClick={handleCloseWarning}
          >
            <Close fontSize="inherit" sx={{ color: "#000" }} />
          </IconButton>
        </Box>
      )}

      <Button
        variant="outlined"
        sx={{ marginTop: "16px" }}
        onClick={handleCancel}
      >
        Cancel
      </Button>
    </Box>
  );
};

const useStyles = makeSxStyles(() => ({
  invalidSelection: {
    width: "100%",
    padding: "16px",
    backgroundColor: "#FFB4AB",
    borderRadius: "4px",
    display: "flex",
    alignItems: "Center",
    justifyContent: "space-between",
    marginTop: "16px",
  },
}));

export default PasswordEdit;
